// @flow

import React, { Component, Fragment } from 'react';
import { Route } from 'react-router-dom';
import { withTranslation, WithTranslation, Translation } from 'react-i18next';
import { connect } from 'react-redux';
import asyncComponent from '../async-component';
import AppView from '../layouts/AppView';
import Text from './Typography';
import ResourceAddressCard from '../resource-address-card';
import FileCard from '../resource-file-card';
import ObservationCards from '../observation-cards';
import Articles from './Articles';
import PrivateComponent from '../shared/PrivateComponent';
import Loader from '../shared/Loader';
import RecordShareAlert from '../shared-resource-banner';
// types
import type { ResourceType } from '../../types/resource';
import type { InstitutionType, InstitutionAddress } from '../../types/institution';
// utils
import { concatTranslations } from '../../utils/format';
import { generateKey } from '../../utils/utils';
import { getRouteTFunction } from '../../i18n';

const AsyncResourceFile = asyncComponent(() => import('../resource-file'));

type Props = {
  provider: InstitutionType,
  fetchProvider: (id: number) => InstitutionType,
  fetchProviderAddress: (id: number) => InstitutionAddress,
  resource: ResourceType,
  providerAddress: {},
  institution: {},
  providerAddressID: number,
  viewType?: string,
  fileviewerReg: boolean,
  location: {},
  match: {},
  loading?: boolean,
  sharedAt?: string,
  token?: string,
  resourceShared?: any
} & WithTranslation

class RecordDetails extends Component {
  getResource = (rec, type) =>
    rec.nested && rec.nested.filter((r) => r.resource_type === type)

  getAllResources = (rec) =>
    rec.nested && rec.nested.filter((r) =>
      r.resource_type !== 'medicationDispense' && r.resource_type !== 'observation')

  props: Props

  render() {
    const { viewType, fileviewerReg, location,
      provider, providerAddress, sharedAt, token, resourceShared, t } = this.props;

    const resource = token ? resourceShared && resourceShared.data
      : this.props.resource && this.props.resource.data;

    const loading = token ? resourceShared && resourceShared.loading
      : this.props.resource && this.props.resource.loading;
    const { title, files, generatedText, type, nestedTypes, resource_type, color } = resource && resource; // eslint-disable-line
    const GeneratedText = generatedText;
    const identifier = resource.identifier || (
      <Translation ns='common'>
        {(commonT) => commonT('unavailable')}
      </Translation>
    );

    // slightly modified view for prescriptions
    const prescription = resource_type === 'medicationRequest'; // eslint-disable-line
    // specialty view for these resources
    const observations = this.getResource(resource, 'observation');
    const rxHistory = this.getResource(resource, 'medicationDispense');
    // all other resources get this generic view
    const remainingNestedResources = this.getAllResources(resource);
    const routesTFunction = getRouteTFunction();

    return (
      resource &&
        <AppView
          viewType={viewType || 'private-body'}
          pageSubtitle={prescription && concatTranslations('Rx # ', identifier)}
          pageTitle={title}
          headerChildren={
            // eslint-disable-next-line camelcase
            <Text span className='record-detail__types'>{type}</Text>
          }
          headerClass={`record-detail__header is-bg-${color}`}
          // for shared records
          frameless={token}
          shareBanner={token &&
            <RecordShareAlert
              shared_at={sharedAt}
              user={resource.user}
              token={token}
            />
          }
        >

          <main className='content-container record-detail wide'>
            {loading && <Loader isLoading />}

            {/* SIDEBAR */}
            <aside className='record-detail__sidebar'>
              {providerAddress && provider &&
                <ResourceAddressCard name={provider && provider.institution_name} address={providerAddress} />}
              <div>
                {files && files.length > 0 &&
                  <Text h3 className='record-detail__section-title'>{files.length} {t('files')}</Text>}
                {files && files.map((file, i) => (
                  <FileCard
                    token={token}
                    key={i}
                    recordID={resource && resource.id}
                    file={file}
                    openFile={this.openViewer}
                  />
                ))}
              </div>
            </aside>

            {/* BODY */}
            <section className='record-detail__section'>
              {generatedText &&
                <div className='record-detail__generated-notes'>
                  <GeneratedText />
                </div>}
              {rxHistory && rxHistory.length > 0 &&
                <Text h3 className='record-detail__section-title'>{t('prescription_history')}</Text>}
              {rxHistory && rxHistory.length > 0 &&
                <div className='record-detail__generated-rx-notes'>
                  {rxHistory.map((history) => {
                    if (history.generatedText) {
                      const HistoryGeneratedText = history.generatedText;
                      return <HistoryGeneratedText key={generateKey()} />;
                    }
                    return null;
                  })}
                </div>}
              {observations && observations.length > 0 &&
                <Text h3 className='record-detail__section-title'>{t('lab_results')}</Text>}
              {observations && observations.length > 0 &&
                <ObservationCards observations={observations} />}
              {remainingNestedResources && remainingNestedResources.length > 0 &&
                <div className='record-detail__generated-rx-notes'>
                  {remainingNestedResources.map((res) => (
                    <Fragment>
                      <Text h3 className='record-detail__section-title'>
                        {type}
                      </Text>
                      {res.generatedText && <GeneratedText />}
                      {res.files && res.files.map((file, j) => (
                        <FileCard
                          token={token}
                          key={j}
                          recordID={res && res.id}
                          file={file}
                          openFile={this.openViewer}
                        />
                      ))}
                    </Fragment>
                  ))}
                </div>}
              {resource && resource.resource_type &&
                <Articles
                  resourceType={resource.resource_type}
                  nestedResourceTypes={nestedTypes}
                  shared={!!token}
                />}
            </section>

            {/* FILE VIEWER -- REGULAR */}
            {fileviewerReg &&
              <Route
                exact
                path={routesTFunction('routerPaths./resource/:id/resource-files/:file_id')}
                component={PrivateComponent(AsyncResourceFile)}
                location={location}
              />
            }
          </main>
        </AppView>
    );
  }
}

const mapStateToProps = (appState) => ({
  resource: appState.resource || appState.sharedResource,
  resourceShared: appState.sharedResource,
  sharedAt: appState.shared && appState.shared.data && appState.shared.data.shared_at
});

const mapDispatchToProps = () => ({
});

export default withTranslation('recordDetails')(
  connect(mapStateToProps, mapDispatchToProps)(RecordDetails)
);



// WEBPACK FOOTER //
// ./src/components/shared/RecordDetails.js